<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"

enum NotificationType {
  RECOGNITION_NOTICE = "recognition-notice",
  REJECTION_NOTICE = "rejection-notice",
}

const operationStore = useOperationStore()

const schema = yup.object().shape({
  notificationType: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "notification-validation-based-on-mode",
      "Ungültiger Wert",
      yupTestOnlyOnCreate((value) =>
        Object.values(NotificationType).includes(value as NotificationType)
      )
    ),

  partialAccess: yup.boolean().when("notificationType", {
    is: NotificationType.RECOGNITION_NOTICE,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),

  shipped: yup.boolean().when("notificationType", {
    is: (value: string) => value !== undefined,
    then: (s) =>
      s.test(
        "is-checked",
        "Der Versand muss bestätigt werden",
        yupTestOnlyOnCreate((value) => value === true)
      ),
    otherwise: (s) => s.strip(),
  }),
  shippingConfirmationDate: yup.string().when("shipped", {
    is: true,
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

watch(
  () => state.notificationType,
  (value) => {
    if (value === NotificationType.REJECTION_NOTICE) {
      state.partialAccess = undefined
    }
  }
)

watch(
  () => state.shipped,
  (value) => {
    if (value === false) {
      state.shippingConfirmationDate = undefined
    }
    operationStore.unsavedChanges = false
  },
  { immediate: true }
)
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Bescheid"
      />
    </template>

    <IffContentSection headline="Bestätigen" :is-last-child="true">
      <IffDualRadioGroup
        name="notificationType"
        v-model="state.notificationType"
        legend="Bescheidtyp"
        required
        :options="[
          {
            label: 'Anerkennungsbescheid',
            value: NotificationType.RECOGNITION_NOTICE,
          },
          {
            label: 'Ablehnungsbescheid',
            value: NotificationType.REJECTION_NOTICE,
          },
        ]"
        aria-controls="partial-access-region shipped-region"
      />
      <div id="partial-access-region" aria-live="polite">
        <IffTransitionHeight>
          <IffDualRadioGroup
            v-if="
              state.notificationType === NotificationType.RECOGNITION_NOTICE
            "
            name="partialAccess"
            v-model="state.partialAccess"
            legend="Partieller Zugang?"
            required
          />
        </IffTransitionHeight>
      </div>

      <div id="shipped-region" aria-live="polite">
        <IffTransitionHeight>
          <div v-if="state.notificationType">
            <UFormGroup name="shipped" class="w-[600px]">
              <UCheckbox
                label="Alle für die Bescheiderstellung relevanten Unterlagen liegen vor und der Versand der erforderlichen Dokumente an die antragstellende Person ist erfolgt"
                v-model="state.shipped"
                required
                aria-required="true"
                aria-controls="shipping-confirmation-date-region"
              />
            </UFormGroup>
            <div id="shipping-confirmation-date-region" aria-live="polite">
              <IffTransitionHeight>
                <UFormGroup
                  name="shippingConfirmationDate"
                  label="Datum Bescheid"
                  v-if="state.shipped"
                  required
                  class="max-w-[185px]"
                >
                  <template #hint>
                    <IffIconTooltip
                      text="BQFG: Wert wird in Datum der Entscheidung (e08) bzw. in Datum der endgültigen Entscheidung (e17) übernommen."
                    />
                    <UButton
                      @click="
                        state.shippingConfirmationDate = new Date()
                          .toISOString()
                          .slice(0, 10)
                      "
                      icon="i-ic-outline-access-time"
                      size="sm"
                      color="gray"
                      variant="ghost"
                      :trailing="false"
                      aria-label="Aktuelles Datum wählen"
                      title="Aktuelles Datum wählen"
                    />
                  </template>

                  <UInput
                    v-model="state.shippingConfirmationDate"
                    type="date"
                    aria-required="true"
                    class="whitespace-normal max-w-[150px]"
                  />
                </UFormGroup>
              </IffTransitionHeight>
            </div>
          </div>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
